import Sentry from "@similarweb/sentry-logger";

import { DefaultFetchService } from "app/services/fetchService";
import { updateWindowUser } from "single-spa/bootstrap-utils";

const ENDPOINT = "api/userdata/preferences";
const REMOVE_ENDPOINT = ENDPOINT + "/remove-bulk";
const ADD_ENDPOINT = ENDPOINT + "/add";

type IPreferenceValue = any;
type IPreferenceKey = string;
type IPreferences = Record<IPreferenceKey, IPreferenceValue>;

class PreferencesServiceClass {
    private static instance: PreferencesServiceClass;

    private preferences: IPreferences = {};
    private _isDirty = false;

    private constructor() {
        void window.similarwebPromises?.preferencesPromise
            ?.then((res: Response) => (res?.ok ? (res.json() as Promise<IPreferences>) : null))
            .then((data: IPreferences | null) => {
                if (!this._isDirty) {
                    const preferences = data ?? {};
                    this.init(preferences);
                    updateWindowUser("preferences", preferences);
                }
            })
            .catch((err) => {
                Sentry.errorTracker({
                    message: "[PreferencesService] hydration failed, using defaults",
                    e: err,
                    level: "warning",
                });
            });
    }

    static getInstance(): PreferencesServiceClass {
        if (!PreferencesServiceClass.instance) {
            PreferencesServiceClass.instance = new PreferencesServiceClass();
        }
        return PreferencesServiceClass.instance;
    }

    private updateLocal(promise: Promise<IPreferences>): void {
        this._isDirty = true;
        void promise
            .then((newPreferences) => {
                this.preferences = newPreferences;
            })
            .catch(() => {
                // rejection is handled by the caller via the returned promise
            });
    }

    init(preferencesArg: IPreferences = {}): void {
        this.preferences = preferencesArg;
    }

    get(key?: IPreferenceKey): IPreferenceValue {
        return key ? this.preferences[key] : this.preferences;
    }

    remove(keys: IPreferenceKey[]): Promise<IPreferences> {
        const fetchService = DefaultFetchService.getInstance();
        const promise = fetchService.post<IPreferences>(REMOVE_ENDPOINT, keys);
        this.updateLocal(promise);
        return promise;
    }

    add<T = IPreferences>(dictionary: T): Promise<T> {
        const fetchService = DefaultFetchService.getInstance();
        const promise = fetchService.post<T>(ADD_ENDPOINT, dictionary as unknown as IPreferences);
        this.updateLocal(promise as unknown as Promise<IPreferences>);
        return promise;
    }
}

export const PreferencesService = PreferencesServiceClass.getInstance();
